/**
 * Sanskrit / non-English pose-term matching for in-prose hyperlinks.
 * Pocket Yoga spellings (shavasana, shirshasana, padangushtha) differ from
 * common Ashtanga latinizations (savasana, sirsasana, padangustha) — fold both.
 */

import { POSE_INDEX } from './poseIndex.gen';
import { POSE_DETAILS } from './poseDetails.gen';
import { poseThumb, POSE_SHORTCUTS } from './poseImages';

const SHORTCUT_BY_NAME = Object.fromEntries(
  Object.entries(POSE_SHORTCUTS).map(([short, name]) => [name, short]),
);

/** ASCII fold used for equality (not for display). */
export function foldPoseKey(s) {
  let n = String(s || '')
    .toLowerCase()
    .normalize('NFKD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/['’]/g, '');

  // Normalize whitespace before stripping punctuation so "Virabhadrasana II" stays tokenizable.
  n = n.replace(/\s+/g, ' ').trim();

  // Common latinization variants.
  n = n.replace(/parivrtta/g, 'parivritta');
  n = n.replace(/trianga/g, 'triang');
  n = n.replace(/upavishta/g, 'upavista');
  n = n.replace(/upavistha/g, 'upavista');

  // Warrior / series roman numerals or digits → a/b/c/d letters.
  n = n.replace(/\b(iii|3)\b/g, 'c');
  n = n.replace(/\b(ii|2)\b/g, 'b');
  n = n.replace(/\b(iv|4)\b/g, 'd');
  n = n.replace(/\b(i|1)\b/g, 'a');

  n = n.replace(/[^a-z0-9]+/g, '');

  // Aspirated consonants → unaspirated (shavasana ↔ savasana).
  n = n
    .replace(/sh/g, 's')
    .replace(/kh/g, 'k')
    .replace(/gh/g, 'g')
    .replace(/ch/g, 'c')
    .replace(/jh/g, 'j')
    .replace(/th/g, 't')
    .replace(/dh/g, 'd')
    .replace(/ph/g, 'p')
    .replace(/bh/g, 'b')
    .replace(/aa/g, 'a')
    .replace(/ee/g, 'i')
    .replace(/oo/g, 'u');

  return n;
}

function visibilityWeight(pose) {
  if (pose.visibility === 'primary') return 3;
  if (pose.visibility === 'secondary') return 2;
  return 1;
}

const BY_FOLD = new Map();

function addFold(key, pose, bonus = 0) {
  const f = foldPoseKey(key);
  if (!f || f.length < 4) return;
  const weight = visibilityWeight(pose) + bonus;
  const prev = BY_FOLD.get(f);
  if (!prev || weight > prev.weight) {
    BY_FOLD.set(f, { pose, weight });
  }
}

/** Strip leading honorific / directional words for alternate keys. */
function strippedVariants(phrase) {
  const raw = String(phrase || '').trim();
  if (!raw) return [];
  const out = [raw];
  const stripped = raw.replace(
    /^(utthita|parivrtta|parivritta|salamba|supta|ardha|baddha|urdhva|adho mukha)\s+/i,
    '',
  );
  if (stripped !== raw) out.push(stripped);
  // Drop trailing series letter (a/b/c/d).
  const noLetter = raw.replace(/\s+[a-d]$/i, '');
  if (noLetter !== raw) out.push(noLetter);
  const strippedNoLetter = stripped.replace(/\s+[a-d]$/i, '');
  if (strippedNoLetter !== stripped && strippedNoLetter !== raw) {
    out.push(strippedNoLetter);
  }
  return out;
}

for (const pose of POSE_INDEX) {
  addFold(pose.name, pose);
  addFold(pose.displayName, pose);
  addFold(pose.sortName, pose);
  for (const a of pose.aka || []) addFold(a, pose);
  for (const s of pose.sanskrit || []) {
    const variants = strippedVariants(s);
    for (const v of variants) {
      // Exact dictionary spelling outranks stripped prefixes ("supta …" → bare asana).
      addFold(v, pose, v === s ? 3 : 0);
    }
  }
}

// Extra common lesson spellings → Pocket Yoga name.
const EXTRA_ALIASES = {
  samasthiti: 'Mountain',
  'surya namaskar': 'DownwardDog',
  chaturanga: 'FourLimbedStaff',
  'setu bandha': 'Bridge',
  'setu bandhasana': 'Bridge',
  sarvangasana: 'ShoulderstandSupported',
  sirsasana: 'HeadstandSupported',
  shirshasana: 'HeadstandSupported',
  'utthita trikonasana': 'TriangleForward',
  'virabhadrasana ii': 'WarriorII',
  'virabhadrasana 2': 'WarriorII',
  'triang mukha eka pada paschimottanasana': 'SeatedForwardBendThreeLimbs',
  'trianga mukha eka pada paschimottanasana': 'SeatedForwardBendThreeLimbs',
};

for (const [alias, name] of Object.entries(EXTRA_ALIASES)) {
  const pose = POSE_INDEX.find((p) => p.name === name);
  if (pose) addFold(alias, pose, 4);
}

/**
 * Look up a pose by English, aka, or Sanskrit (with spelling fold).
 * @returns {object|null} Pocket Yoga index entry
 */
export function lookupPoseTerm(ref) {
  const key = String(ref || '').trim();
  if (!key) return null;
  const hit = BY_FOLD.get(foldPoseKey(key));
  if (hit) return hit.pose;
  // Try without trailing series letter / roman numeral already folded.
  for (const v of strippedVariants(key)) {
    const h = BY_FOLD.get(foldPoseKey(v));
    if (h) return h.pose;
  }
  return null;
}

/** Phrases to scan in prose — longest folded key first. */
function buildTermList() {
  const byFold = new Map();

  function push(phrase, pose, bias = 0) {
    const display = String(phrase || '').trim();
    if (!display || display.length < 4) return;
    const f = foldPoseKey(display);
    if (!f || f.length < 4) return;
    const looksSanskrit =
      /asana|mudra|namaskar|samasthiti|bandha|konasana|padasana|sirsasana|shirshasana|savasana|shavasana|tadasana|uttanasana|virabhadrasana|trikonasana|parsvakonasana|parshvakonasana|padottanasana|paschimottanasana|purvottanasana|marichyasana|navasana|halasana|sarvangasana|dandasana|balasana|ujjayi|chaturanga|svanasana|shvanasana|padangustha|padangushtha|parsvottanasana|parshvottanasana/i.test(
        display,
      );
    if (!looksSanskrit) return;
    const prev = byFold.get(f);
    const weight = visibilityWeight(pose) + bias + display.length / 100;
    if (!prev || weight > prev.weight) {
      byFold.set(f, { phrase: display, fold: f, pose, weight });
    }
  }

  for (const pose of POSE_INDEX) {
    for (const s of pose.sanskrit || []) {
      for (const v of strippedVariants(s)) {
        // Exact dictionary spelling outranks stripped prefixes ("supta …" → bare asana).
        push(v, pose, v === s ? 1 : 0);
      }
    }
  }
  for (const alias of Object.keys(EXTRA_ALIASES)) {
    const pose = lookupPoseTerm(alias);
    if (pose) push(alias, pose, 2);
  }

  return [...byFold.values()].sort((a, b) => b.fold.length - a.fold.length);
}

const TERM_LIST = buildTermList();
const TERM_BY_FOLD = new Map(TERM_LIST.map((t) => [t.fold, t]));

function isWordChar(ch) {
  return ch != null && /[A-Za-z0-9]/.test(ch);
}

function isTermGlue(ch) {
  // Spaces / apostrophes only — en-dash ranges like "A–C" stay outside the term.
  return ch != null && /[\s'’]/.test(ch);
}

/**
 * Split text into plain + pose-term segments for hyperlinking.
 * Matching uses phonetic fold so shavasana / savasana both hit.
 * @returns {{ type: 'text'|'term', value: string, pose?: object }[]}
 */
export function segmentPoseTerms(text) {
  const src = String(text || '');
  if (!src) return [];

  const candidates = [];
  for (let start = 0; start < src.length; start += 1) {
    if (!isWordChar(src[start])) continue;
    if (start > 0 && isWordChar(src[start - 1])) continue;

    for (let end = start + 1; end <= Math.min(src.length, start + 72); end += 1) {
      const atEnd = end === src.length;
      const next = atEnd ? '' : src[end];
      // Only consider spans that end on a word boundary.
      if (!atEnd && isWordChar(next)) continue;

      const slice = src.slice(start, end);
      const f = foldPoseKey(slice);
      if (f.length >= 4) {
        const term = TERM_BY_FOLD.get(f);
        if (term) {
          candidates.push({
            start,
            end,
            pose: term.pose,
            foldLen: f.length,
          });
        }
      }

      if (!atEnd && !isWordChar(next) && !isTermGlue(next)) break;
    }
  }

  // Longest fold wins; then leftmost. Skip overlaps.
  candidates.sort((a, b) => b.foldLen - a.foldLen || a.start - b.start);
  const claimed = new Array(src.length).fill(false);
  const hits = [];
  for (const c of candidates) {
    let overlap = false;
    for (let i = c.start; i < c.end; i += 1) {
      if (claimed[i]) {
        overlap = true;
        break;
      }
    }
    if (overlap) continue;
    for (let i = c.start; i < c.end; i += 1) claimed[i] = true;
    hits.push(c);
  }
  hits.sort((a, b) => a.start - b.start);

  const parts = [];
  let last = 0;
  for (const hit of hits) {
    if (hit.start > last) {
      parts.push({ type: 'text', value: src.slice(last, hit.start) });
    }
    parts.push({
      type: 'term',
      value: src.slice(hit.start, hit.end),
      pose: hit.pose,
    });
    last = hit.end;
  }
  if (last < src.length) parts.push({ type: 'text', value: src.slice(last) });
  return parts.length ? parts : [{ type: 'text', value: src }];
}

/** Card model for the pop-up. */
export function poseTermCard(poseOrRef) {
  const pose =
    typeof poseOrRef === 'string' ? lookupPoseTerm(poseOrRef) : poseOrRef;
  if (!pose) return null;
  const detail = POSE_DETAILS[pose.name] || {};
  const thumb = poseThumb(pose);
  const shortcut = SHORTCUT_BY_NAME[pose.name];
  return {
    pose,
    displayName: pose.displayName,
    sanskrit: (pose.sanskrit || []).join(' · '),
    description: detail.description || '',
    benefits: detail.benefits || '',
    thumb,
    cueKey: shortcut || pose.name,
    difficulty: pose.difficulty,
    category: pose.category,
  };
}
